"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isRecurringJob = exports.isJobWindowClosed = exports.RequestedBy = exports.TargetEnumerationSanityLimit = exports.TargetEnumerationPageSize = exports.StalledNoProgressMs = exports.MaxTargetAttempts = exports.MaxTimeoutRetries = exports.MinRetryInterval = exports.Protocol = exports.JobNotificationTriggers = exports.JobTaskTargetStatus = exports.RetriableTargetStatuses = exports.JobTargetStatus = exports.ErrorCodes = exports.CancelableStates = exports.RestartableStates = exports.JobStatus = exports.RetryPolicy = exports.TargetUpdatePolicy = exports.TargetSelectionMode = exports.TargetType = exports.JobTaskExecCondition = exports.JobTrigger = exports.JobVersion = void 0;
/**
 * Constants and rules of the Jobs Engine, moved here from unified-api's
 * `libraries/jobs.js` so the engine can run outside unified-api. Where a value
 * set mirrors a schema in `Culligan.Job`, `satisfies` keeps the two in step.
 */
/**
 * Version of the Job configuration.
 * Used to spawn the correct worker to process the Job.
 */
exports.JobVersion = {
    V1: 1,
};
/** Triggers to run a Job */
exports.JobTrigger = {
    /** System scheduler */
    Scheduler: 'scheduler',
    /** Run on demand */
    OnDemand: 'onDemand',
};
/** Conditions to execute a Job Task. */
exports.JobTaskExecCondition = {
    /** Always execute */
    Always: 'always',
    /** Execute if all previous tasks were successful */
    AllPreviousSucceded: 'allPrevSucceded',
    /** Execute if the immediately previous task was successful */
    PreviousSucceded: 'prevSucceded',
    /** Execute if all previous tasks were not successful */
    AllPreviousFailed: 'allPrevFailed',
    /** Execute if the immediately previous task was not successful */
    PreviousFailed: 'prevFailed',
};
/** Type of target */
exports.TargetType = {
    /** Device */
    Device: 'device',
};
/**
 * Target Devices selection mode.
 * Not checked against `Culligan.Job.TargetSelectionMode`, which no longer
 * lists `deviceGroup`: stored jobs may still carry it.
 */
exports.TargetSelectionMode = {
    /** OTA Group (pending deprecation) */
    DeviceGroup: 'deviceGroup',
    /** Device search filter */
    Filter: 'filter',
};
/** Target Devices update policy. */
exports.TargetUpdatePolicy = {
    /** Defer the selection to the first job run */
    FirstRun: 'firstRun',
    /** Update before every job run */
    Always: 'always',
};
/** Retry policy */
exports.RetryPolicy = {
    /** Recurring job at given intervals */
    Interval: 'interval',
};
/** Job states */
exports.JobStatus = {
    /**
     * Job was created, the system is verifying
     * and loading the selected devices' data.
     */
    Loading: 'loading',
    /** Job is queued for execution. */
    Queued: 'queued',
    /** Job is running. */
    Running: 'running',
    /** Job is iterating through a list of targets */
    CoolDown: 'coolDown',
    /** Waiting for the next run */
    Waiting: 'waiting',
    /** Job is paused. */
    Paused: 'paused',
    /**
     * Job is completed successfully
     * (for all the listed devices).
     */
    Completed: 'completed',
    /**
     * Job is failed
     * (for all or some devices).
     */
    Failed: 'failed',
    /** Job was canceled by an admin user. */
    Canceled: 'canceled',
};
/**
 * Job states from which an admin can force a restart.
 * Membership means "restartable". The admin controllers and the
 * job service both reference this single list so their checks
 * cannot drift apart (the inverse logic was the cause of IOTPE-575).
 * `Completed`, `Running` and `Loading` are intentionally excluded.
 */
exports.RestartableStates = Object.freeze([
    exports.JobStatus.CoolDown,
    exports.JobStatus.Failed,
    exports.JobStatus.Waiting,
    exports.JobStatus.Queued,
    exports.JobStatus.Paused,
    exports.JobStatus.Canceled,
]);
/**
 * Job states from which an admin can cancel a Job: every state but the final
 * ones, so a finished Job keeps the outcome it ended with.
 */
exports.CancelableStates = Object.freeze([
    exports.JobStatus.Loading,
    exports.JobStatus.Queued,
    exports.JobStatus.Running,
    exports.JobStatus.CoolDown,
    exports.JobStatus.Waiting,
    exports.JobStatus.Paused,
]);
/**
 * Error codes shared by the job service and job admin APIs.
 */
exports.ErrorCodes = Object.freeze({
    CannotCancelJob: 'CANNOT_CANCEL_JOB',
    CannotRestartJob: 'CANNOT_RESTART_JOB',
    CannotScheduleJob: 'CANNOT_SCHEDULE_JOB',
    TargetLimitExceeded: 'TARGET_LIMIT_EXCEEDED',
});
/** States of a Job on a Target. */
exports.JobTargetStatus = {
    /** Job queued */
    Queued: 'queued',
    /** Target not found */
    NotFound: 'notFound',
    /** Target offline */
    Offline: 'offline',
    /** Target has a temporary error */
    TemporaryError: 'temporaryError',
    /** Instructions delivered to Target */
    Delivered: 'delivered',
    /** Job completed on Target */
    Completed: 'completed',
    /** Job failed on Target */
    Failed: 'failed',
};
/**
 * Target statuses that an admin restart re-opens (re-queues with a fresh
 * attempt budget). The successful states (`Delivered`, `Completed`) are
 * deliberately excluded so already-updated devices are not re-sent.
 */
exports.RetriableTargetStatuses = Object.freeze([
    exports.JobTargetStatus.Failed,
    exports.JobTargetStatus.NotFound,
    exports.JobTargetStatus.Offline,
    exports.JobTargetStatus.TemporaryError,
]);
/** States of a Job Task on an individual Target. */
exports.JobTaskTargetStatus = {
    /** Job queued */
    Queued: 'queued',
    /** Target not found */
    NotFound: 'notFound',
    /** Target offline */
    Offline: 'offline',
    /** Target has a temporary error */
    TemporaryError: 'temporaryError',
    /** Instructions delivered to Target */
    Delivered: 'delivered',
    /** Job Action completed on Target */
    Completed: 'completed',
    /** Job Action failed on Target */
    Failed: 'failed',
    /** Job Action rejected by Target */
    Rejected: 'rejected',
    /** Conditions not met */
    ConditionsNotMet: 'conditionsNotMet',
};
/** Triggers for notifications */
exports.JobNotificationTriggers = {
    /* Job completed */
    Completed: 'completed',
    /* Job failed */
    Failed: 'failed',
};
/** Job protocol commands */
exports.Protocol = {
    Commands: {
        /** Loads the Job data and prepare the execution */
        Load: 'load',
        /** Runs the next step of a Job */
        Run: 'run',
    },
};
/** Minimum Retry Interval (in seconds) */
exports.MinRetryInterval = 600;
/** Maximum number of timeout recoveries before marking job as failed */
exports.MaxTimeoutRetries = 3;
/** Maximum number of attempts per target before marking target as failed */
exports.MaxTargetAttempts = 3;
/**
 * How long a recurring rollout may deliver to zero devices before it is flagged as
 * stalled. Time-based, not pass-based, so it doesn't swing with retryInterval (600s
 * floor .. hours). A partially-delivered rollout is never flagged.
 */
exports.StalledNoProgressMs = 24 * 60 * 60 * 1000;
/** Page size for paginating a Job's target Devices during enumeration. */
exports.TargetEnumerationPageSize = 1000;
/**
 * Maximum number of target Devices a filter-based Job may enumerate without an
 * explicit large-rollout override. Above this the Job fails loud instead of
 * enqueueing a fleet-wide rollout from an over-broad filter.
 */
exports.TargetEnumerationSanityLimit = 75000;
/** Job request source */
exports.RequestedBy = {
    /** The Device Owner User requested the Job */
    User: 'user',
    /** The Device requested the Job */
    Device: 'device',
    /** An Admin requested the Job */
    Admin: 'admin',
};
/**
 * Returns `true` when the Job's run window has closed, i.e. it must NOT be
 * scheduled or continued for another pass. Single source of truth shared by
 * the job worker (`updateRunStatus`) and the job service (`shouldScheduleNextRun`)
 * so the two cannot drift apart.
 *
 * Three mutually exclusive modes:
 *  - one-shot (no `retryPolicy`): done after the first completed pass, or once
 *    `stopAt` has passed (preserving the legacy stopAt gate for all jobs).
 *  - continuous with a `stopAt`: the window is bounded ONLY by `stopAt`.
 *    `maxAttempts` is deliberately NOT treated as a pass counter here - a
 *    recurring OTA job must keep making a pass every `retryInterval` until
 *    `stopAt`, regardless of how many passes ran or their per-pass result.
 *    Per-device retry is bounded per pass by `maxTargetAttempts`; a recurring pass
 *    re-opens unsuccessful Targets with a fresh budget, so a device is re-attempted
 *    anew each pass within the window.
 *  - interval without a `stopAt`: `maxAttempts` is the safety cap that keeps an
 *    open-ended recurring job from scheduling forever.
 *
 * @param job Job
 * @param attempts Number of completed passes so far
 */
const isJobWindowClosed = (job, attempts) => {
    if (!job.retryPolicy) {
        return attempts > 0 || (Boolean(job.stopAt) && Date.parse(String(job.stopAt)) <= Date.now());
    }
    if (job.stopAt) {
        // Fail-safe: this predicate is the single source of truth, so an
        // unparseable (legacy/corrupt) stopAt must close the window rather than
        // return false forever and skip the maxAttempts cap below (unbounded job).
        const stop = Date.parse(String(job.stopAt));
        return Number.isNaN(stop) ? true : stop <= Date.now();
    }
    return Boolean(job.maxAttempts) && attempts >= (job.maxAttempts ?? 0);
};
exports.isJobWindowClosed = isJobWindowClosed;
/**
 * Returns `true` when the Job is a recurring (interval) rollout, i.e. it makes
 * a fresh pass every `retryInterval` until its window closes. Single source of
 * truth so the "is this recurring?" check does not drift across the job service
 * and worker.
 * @param job Job
 */
const isRecurringJob = (job) => job.retryPolicy === exports.RetryPolicy.Interval;
exports.isRecurringJob = isRecurringJob;
//# sourceMappingURL=job-engine.js.map